import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllClients } from '../utils/apiService';
import { calculateClientCompletion } from '../utils/completion/clientCompletionCalculator';
import { ExclamationTriangleIcon, ServerIcon, CheckCircleIcon, InformationCircleIcon, CircleStackIcon, SignalIcon, ShieldCheckIcon, ChartBarIcon } from '@heroicons/react/24/outline';

const InfrastructureOverview = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'servers', 'completion'
  const [sortBy, setSortBy] = useState('critical');
  const [showDetails, setShowDetails] = useState({});

  useEffect(() => {
    loadClientsAndAnalyze();
  }, []);

  const loadClientsAndAnalyze = async () => {
    try {
      const clientData = await getAllClients();
      setClients(clientData);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeInfrastructure = () => {
    const clientsWithAnalysis = clients.map(client => {
      const completion = calculateClientCompletion(client);
      return {
        ...client,
        completion,
        hasIncompleteServers: completion.serverAnalysis?.incompleteServers?.length > 0,
        criticalIssuesCount: completion.serverAnalysis?.criticalIssues?.length || 0,
        warningIssuesCount: completion.serverAnalysis?.warningIssues?.length || 0,
        serverCompletionScore: completion.serverAnalysis?.completionScore || 0
      };
    });

    const stats = {
      total: clientsWithAnalysis.length,
      totalServers: clientsWithAnalysis.reduce((sum, client) => sum + (client.servers?.length || 0), 0),
      
      // Completion stats
      complete: clientsWithAnalysis.filter(c => c.completion.percentage === 100).length,
      incomplete: clientsWithAnalysis.filter(c => c.completion.percentage < 100).length,
      withIncompleteServers: clientsWithAnalysis.filter(c => c.hasIncompleteServers).length,
      totalIncompleteServers: clientsWithAnalysis.reduce((sum, c) => 
        sum + (c.completion.serverAnalysis?.incompleteServers?.length || 0), 0
      ),
      
      // Probes stats
      probeStats: { missing: [], configured: [], notSet: [] },
      
      // DB stats
      dbStats: { unoptimized: [], optimized: [], notSet: [] },
      
      // Accessibility stats
      accessibilityStats: { external: [], internal: [], notSet: [] }
    };

    // Analyze each client in detail
    clientsWithAnalysis.forEach(client => {
      if (client.servers && client.servers.length > 0) {
        client.servers.forEach((server, serverIndex) => {
          const serverInfo = {
            client: client.name,
            clientId: client.id,
            serverName: server.name || `Serveur ${serverIndex + 1}`,
            serverType: server.type
          };

          // Probe analysis
          if (server.probeDeployed === true) {
            stats.probeStats.configured.push(serverInfo);
          } else if (server.probeDeployed === false) {
            stats.probeStats.missing.push(serverInfo);
          } else {
            stats.probeStats.notSet.push(serverInfo);
          }

          // DB analysis (skip for application servers)
          if (server.type !== 'Serveur applicatif') {
            if (server.dbOptimized === true) {
              stats.dbStats.optimized.push(serverInfo);
            } else if (server.dbOptimized === false) {
              stats.dbStats.unoptimized.push(serverInfo);
            } else {
              stats.dbStats.notSet.push(serverInfo);
            }
          }
        });
      }

      // URL accessibility analysis
      if (client.patientPortalUrl || client.carePortalUrl) {
        const hasExternalAccess = 
          client.patientPortalUrlExternallyAccessible === true ||
          client.carePortalUrlExternallyAccessible === true;
        
        const hasInternalOnly = 
          client.patientPortalUrlExternallyAccessible === false &&
          client.carePortalUrlExternallyAccessible === false;

        const notSet = 
          client.patientPortalUrlExternallyAccessible === undefined ||
          client.carePortalUrlExternallyAccessible === undefined;

        const urlInfo = {
          client: client.name,
          clientId: client.id,
          patientExternal: client.patientPortalUrlExternallyAccessible,
          careExternal: client.carePortalUrlExternallyAccessible,
          patientUrl: client.patientPortalUrl,
          careUrl: client.carePortalUrl
        };

        if (hasExternalAccess) {
          stats.accessibilityStats.external.push(urlInfo);
        } else if (hasInternalOnly) {
          stats.accessibilityStats.internal.push(urlInfo);
        } else if (notSet) {
          stats.accessibilityStats.notSet.push(urlInfo);
        }
      }
    });

    // Filter incomplete servers
    const incompleteClients = clientsWithAnalysis.filter(client => client.hasIncompleteServers);
    
    // Sort incomplete clients
    incompleteClients.sort((a, b) => {
      switch (sortBy) {
        case 'critical':
          return b.criticalIssuesCount - a.criticalIssuesCount;
        case 'completion':
          return a.serverCompletionScore - b.serverCompletionScore;
        case 'name':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return {
      stats,
      clients: clientsWithAnalysis,
      incompleteClients
    };
  };

  const toggleDetails = (clientId) => {
    setShowDetails(prev => ({
      ...prev,
      [clientId]: !prev[clientId]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const analysis = analyzeInfrastructure();
  const { stats, clients: clientsWithAnalysis, incompleteClients } = analysis;

  const TabButton = ({ id, label, count = null, icon: Icon, isActive }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`flex items-center px-6 py-3 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
        isActive
          ? 'text-blue-600 border-blue-600 bg-blue-50'
          : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {Icon && <Icon className="h-4 w-4 mr-2" />}
      {label}
      {count !== null && (
        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full font-medium ${
          isActive ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Vue d'ensemble Infrastructure</h1>
        <div className="text-sm text-gray-500">
          Dernière analyse: {new Date().toLocaleString('fr-FR')}
        </div>
      </div>

      {/* Global Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <ChartBarIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Clients Total</p>
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center">
            <ServerIcon className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Serveurs Total</p>
              <p className="text-2xl font-bold text-purple-600">{stats.totalServers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Complets</p>
              <p className="text-2xl font-bold text-green-600">{stats.complete}</p>
              <p className="text-xs text-gray-500">{Math.round(stats.complete / stats.total * 100)}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-amber-500">
          <div className="flex items-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-amber-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">À Compléter</p>
              <p className="text-2xl font-bold text-amber-600">{stats.withIncompleteServers}</p>
              <p className="text-xs text-gray-500">{stats.totalIncompleteServers} serveur(s)</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow border-l-4 border-red-500">
          <div className="flex items-center">
            <SignalIcon className="h-8 w-8 text-red-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Alertes Critiques</p>
              <p className="text-2xl font-bold text-red-600">
                {stats.probeStats.missing.length + stats.probeStats.notSet.length}
              </p>
              <p className="text-xs text-gray-500">sondes manquantes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <TabButton
              id="overview"
              label="Vue d'ensemble"
              icon={ChartBarIcon}
              isActive={activeTab === 'overview'}
            />
            <TabButton
              id="servers"
              label="Serveurs Incomplets"
              count={incompleteClients.length}
              icon={ServerIcon}
              isActive={activeTab === 'servers'}
            />
            <TabButton
              id="infrastructure"
              label="Infrastructure"
              icon={CircleStackIcon}
              isActive={activeTab === 'infrastructure'}
            />
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Quick Actions */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">Actions Rapides</h3>
                  <div className="space-y-3">
                    {stats.withIncompleteServers > 0 && (
                      <button
                        onClick={() => setActiveTab('servers')}
                        className="w-full text-left p-3 bg-amber-100 border border-amber-300 rounded-lg hover:bg-amber-200 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-amber-800">🖥️ Compléter les serveurs</p>
                            <p className="text-sm text-amber-600">{stats.withIncompleteServers} client(s) avec serveurs incomplets</p>
                          </div>
                          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                        </div>
                      </button>
                    )}

                    {(stats.probeStats.missing.length + stats.probeStats.notSet.length) > 0 && (
                      <button
                        onClick={() => setActiveTab('infrastructure')}
                        className="w-full text-left p-3 bg-red-100 border border-red-300 rounded-lg hover:bg-red-200 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-red-800">🚨 Déployer les sondes</p>
                            <p className="text-sm text-red-600">
                              {stats.probeStats.missing.length + stats.probeStats.notSet.length} serveur(s) sans monitoring
                            </p>
                          </div>
                          <SignalIcon className="h-5 w-5 text-red-600" />
                        </div>
                      </button>
                    )}

                    <Link
                      to="/version-dashboard"
                      className="block w-full text-left p-3 bg-blue-100 border border-blue-300 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-blue-800">📊 Tableau de bord versions</p>
                          <p className="text-sm text-blue-600">Surveiller les versions obsolètes</p>
                        </div>
                        <ChartBarIcon className="h-5 w-5 text-blue-600" />
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Health Summary */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">État de Santé</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Complétion moyenne</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-blue-500 h-2 rounded-full" 
                            style={{ width: `${Math.round(stats.complete / stats.total * 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium">{Math.round(stats.complete / stats.total * 100)}%</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">Sondes déployées</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-green-500 h-2 rounded-full" 
                            style={{ 
                              width: `${Math.round(stats.probeStats.configured.length / (stats.probeStats.configured.length + stats.probeStats.missing.length + stats.probeStats.notSet.length) * 100)}%` 
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium">
                          {stats.probeStats.configured.length}/{stats.probeStats.configured.length + stats.probeStats.missing.length + stats.probeStats.notSet.length}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-gray-700">BDD optimisées</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                          <div 
                            className="bg-purple-500 h-2 rounded-full" 
                            style={{ 
                              width: `${Math.round(stats.dbStats.optimized.length / (stats.dbStats.optimized.length + stats.dbStats.unoptimized.length + stats.dbStats.notSet.length) * 100)}%` 
                            }}
                          />
                        </div>
                        <span className="text-sm font-medium">
                          {stats.dbStats.optimized.length}/{stats.dbStats.optimized.length + stats.dbStats.unoptimized.length + stats.dbStats.notSet.length}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Servers Tab */}
          {activeTab === 'servers' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Serveurs à Compléter ({incompleteClients.length})</h3>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="critical">Trier par criticité</option>
                  <option value="completion">Trier par % complétion</option>
                  <option value="name">Trier par nom</option>
                </select>
              </div>

              {incompleteClients.length === 0 ? (
                <div className="text-center py-12 bg-green-50 rounded-lg">
                  <CheckCircleIcon className="mx-auto h-16 w-16 text-green-600 mb-4" />
                  <h3 className="text-lg font-medium text-green-800 mb-2">Toutes les fiches serveurs sont complètes !</h3>
                  <p className="text-green-600">Excellent travail ! Tous les serveurs sont correctement configurés.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {incompleteClients.map((client) => (
                    <div key={client.id} className="bg-white border border-gray-200 rounded-lg shadow-sm">
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Link 
                              to={`/clients/${client.id}`}
                              className="text-lg font-semibold text-blue-600 hover:underline"
                            >
                              {client.name}
                            </Link>
                            {client.criticalIssuesCount > 0 && (
                              <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full font-medium">
                                {client.criticalIssuesCount} critique(s)
                              </span>
                            )}
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              client.serverCompletionScore >= 80 
                                ? 'bg-blue-100 text-blue-800'
                                : client.serverCompletionScore >= 60 
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                            }`}>
                              {client.serverCompletionScore}% complet
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Link 
                              to={`/edit-client/${client.id}?tab=servers`}
                              className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                            >
                              📝 Modifier
                            </Link>
                            <button
                              onClick={() => toggleDetails(client.id)}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <svg 
                                className={`w-5 h-5 transform transition-transform ${showDetails[client.id] ? 'rotate-180' : ''}`}
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        </div>

                        {showDetails[client.id] && client.completion.serverAnalysis?.incompleteServers && (
                          <div className="mt-4 border-t pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {client.completion.serverAnalysis.incompleteServers.map((server, serverIndex) => (
                                <div key={serverIndex} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                                  <div className="flex items-center justify-between mb-2">
                                    <h5 className="font-medium text-gray-900">{server.name}</h5>
                                    <div className="text-xs text-gray-500">
                                      {server.critical.length + server.warnings.length} problème(s)
                                    </div>
                                  </div>

                                  {server.critical.length > 0 && (
                                    <div className="mb-2">
                                      <h6 className="text-xs font-medium text-red-700 mb-1">🚨 Critiques:</h6>
                                      <div className="flex flex-wrap gap-1">
                                        {server.critical.map((issue, i) => (
                                          <span key={i} className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                                            {issue}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  {server.warnings.length > 0 && (
                                    <div>
                                      <h6 className="text-xs font-medium text-amber-700 mb-1">⚠️ À compléter:</h6>
                                      <div className="flex flex-wrap gap-1">
                                        {server.warnings.map((issue, i) => (
                                          <span key={i} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                                            {issue}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Infrastructure Tab */}
          {activeTab === 'infrastructure' && (
            <div className="space-y-8">
              {/* Probe Status */}
              <div>
                <h3 className="text-lg font-semibold mb-4">État des Sondes de Monitoring</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-2">✅ Configurées ({stats.probeStats.configured.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.probeStats.configured.slice(0, 5).map((item, index) => (
                        <div key={index} className="text-sm">
                          <Link to={`/clients/${item.clientId}`} className="text-green-700 hover:underline">
                            {item.client}
                          </Link>
                          <span className="text-green-600"> • {item.serverName}</span>
                        </div>
                      ))}
                      {stats.probeStats.configured.length > 5 && (
                        <div className="text-sm text-green-600">+{stats.probeStats.configured.length - 5} autres...</div>
                      )}
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="font-medium text-red-800 mb-2">❌ Manquantes ({stats.probeStats.missing.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.probeStats.missing.slice(0, 5).map((item, index) => (
                        <div key={index} className="text-sm">
                          <Link to={`/clients/${item.clientId}`} className="text-red-700 hover:underline">
                            {item.client}
                          </Link>
                          <span className="text-red-600"> • {item.serverName}</span>
                        </div>
                      ))}
                      {stats.probeStats.missing.length > 5 && (
                        <div className="text-sm text-red-600">+{stats.probeStats.missing.length - 5} autres...</div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2">❓ Non définies ({stats.probeStats.notSet.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.probeStats.notSet.slice(0, 5).map((item, index) => (
                        <div key={index} className="text-sm">
                          <Link to={`/clients/${item.clientId}`} className="text-gray-700 hover:underline">
                            {item.client}
                          </Link>
                          <span className="text-gray-600"> • {item.serverName}</span>
                        </div>
                      ))}
                      {stats.probeStats.notSet.length > 5 && (
                        <div className="text-sm text-gray-600">+{stats.probeStats.notSet.length - 5} autres...</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Database Optimization */}
              <div>
                <h3 className="text-lg font-semibold mb-4">État d'Optimisation des Bases de Données</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-2">✅ Optimisées ({stats.dbStats.optimized.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.dbStats.optimized.slice(0, 5).map((item, index) => (
                        <div key={index} className="text-sm">
                          <Link to={`/clients/${item.clientId}`} className="text-green-700 hover:underline">
                            {item.client}
                          </Link>
                          <span className="text-green-600"> • {item.serverName}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-medium text-orange-800 mb-2">⚠️ Non optimisées ({stats.dbStats.unoptimized.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.dbStats.unoptimized.slice(0, 5).map((item, index) => (
                        <div key={index} className="text-sm">
                          <Link to={`/clients/${item.clientId}`} className="text-orange-700 hover:underline">
                            {item.client}
                          </Link>
                          <span className="text-orange-600"> • {item.serverName}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2">❓ Non définies ({stats.dbStats.notSet.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.dbStats.notSet.slice(0, 5).map((item, index) => (
                        <div key={index} className="text-sm">
                          <Link to={`/clients/${item.clientId}`} className="text-gray-700 hover:underline">
                            {item.client}
                          </Link>
                          <span className="text-gray-600"> • {item.serverName}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* URL Accessibility */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Accessibilité des Portails</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-800 mb-2">🌐 Accès externe ({stats.accessibilityStats.external.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.accessibilityStats.external.slice(0, 5).map((item, index) => (
                        <div key={index} className="text-sm">
                          <Link to={`/clients/${item.clientId}`} className="text-blue-700 hover:underline">
                            {item.client}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-medium text-green-800 mb-2">🏠 Interne uniquement ({stats.accessibilityStats.internal.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.accessibilityStats.internal.slice(0, 5).map((item, index) => (
                        <div key={index} className="text-sm">
                          <Link to={`/clients/${item.clientId}`} className="text-green-700 hover:underline">
                            {item.client}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-800 mb-2">❓ Non définies ({stats.accessibilityStats.notSet.length})</h4>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {stats.accessibilityStats.notSet.slice(0, 5).map((item, index) => (
                        <div key={index} className="text-sm">
                          <Link to={`/clients/${item.clientId}`} className="text-gray-700 hover:underline">
                            {item.client}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfrastructureOverview;
